import React, { useContext, useEffect, useRef, useState } from 'react';
import { Modal } from '../Common/Modal';
import Alert from '../Common/Alert';
import { ElectronicSign } from '../Common/ElectronicSign';
import visitService from '../../services/visitService';
import { VISIT_TYPES } from '../../utils/constants';
import { visits as visitMessages, common as commonMessages } from '../../utils/validationMessages';
import { formatDateDDMMMYYYY } from '../../utils/dateFormat';
import { buildLetterFooterSignOff } from '../../utils/letterSignOffFormat';
import { AuthContext } from '../../context/AuthContext';
import VisitModalHeader from './VisitModalHeader';
import './GenerateVisitLetterModal.css';

const MIN_BODY_LENGTH = 20;

const getBodyPlainText = (html) => {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || div.innerText || '').trim();
};

const GenerateVisitLetterModal = ({ isOpen, onClose, visit, resolvedPiName, onGenerated }) => {
  const { user: authUser } = useContext(AuthContext);
  const [editorContent, setEditorContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [letterBodyError, setLetterBodyError] = useState('');
  const [headerText, setHeaderText] = useState('');
  const [footerText, setFooterText] = useState('');
  const bodyEditorRef = useRef(null);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [blockFormat, setBlockFormat] = useState('p');
  const [fontFamily, setFontFamily] = useState('Arial');
  const [showESignModal, setShowESignModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [toast, setToast] = useState(null);
  const eSignRemarksRef = useRef('');
  const saveAndContinueModeRef = useRef(false);
  const bodyContentInitializedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      setHeaderText('');
      setEditorContent('');
      setFooterText('');
      setSaving(false);
      setShowPreview(false);
      setHistory([]);
      setHistoryIndex(-1);
      setBlockFormat('p');
      setFontFamily('Arial');
      setLetterBodyError('');
      bodyContentInitializedRef.current = false;
      return;
    }
    if (!visit?.scheduledVisitID && !visit?.scheduledVisitId) return;

    const visitId = visit.scheduledVisitID ?? visit.scheduledVisitId;
    let cancelled = false;

    const applyDefaultTemplate = (d) => {
      const piName = d?.piName ?? d?.PIName ?? '';
      const projectDescription = d?.projectDescription ?? d?.ProjectDescription ?? '';
      const projectDescriptionLine = projectDescription ? `${projectDescription}\n` : '';
      const siteCode = d?.siteCode ?? d?.SiteCode ?? '';
      const siteAddress = d?.siteName ?? d?.SiteName ?? '';
      const craName = (d?.craName ?? d?.CRAName ?? '').toString().trim();
      const signOff = buildLetterFooterSignOff(authUser) || craName;
      const baseBodyText =
`This visit to your site facility will require approximately 2-4 hours of your research team's time including a meeting with you to discuss the protocol and study requirements.

• Investigational Product / IB discussion
• Discuss the subject recruitment strategy (patient database / referral / etc.)
• Requirements for informed consent procedure
• IRB / IEC requirements
• SAE / AE reporting requirements`;
      const baseHeaderText =
`Date: ${formatDateDDMMMYYYY(new Date())}

To, ${piName}
${projectDescriptionLine}${siteCode}
${siteAddress}

Subject: Confirmation and Agenda of Site Visit.`;
      const baseFooterText =
`Thanking You,
With Regards,
${signOff}`;
      const baseBodyHtml = baseBodyText.replace(/\n/g, '<br />');
      setHeaderText(baseHeaderText);
      setEditorContent(baseBodyHtml);
      setFooterText(baseFooterText);
      setHistory([baseBodyHtml]);
      setHistoryIndex(0);
    };

    visitService.getById(visitId).then((detail) => {
      if (cancelled) return;
      const savedBody = (detail.letterBody != null && String(detail.letterBody).trim() !== '') ? detail.letterBody : null;
      if (savedBody) {
        setHeaderText(detail.letterHeader ?? '');
        setEditorContent(detail.letterBody);
        setFooterText(detail.letterFooter ?? '');
        setHistory([detail.letterBody]);
        setHistoryIndex(0);
      } else {
        applyDefaultTemplate(detail);
      }
      setBlockFormat('p');
      setFontFamily('Arial');
      bodyContentInitializedRef.current = false;
    }).catch((err) => {
      if (!cancelled) {
        console.error('Failed to load visit for letter', err);
        applyDefaultTemplate(visit);
        setBlockFormat('p');
        setFontFamily('Arial');
        bodyContentInitializedRef.current = false;
      }
    });

    return () => { cancelled = true; };
  }, [isOpen, visit, authUser]);

  // Sync state -> DOM only when opening or resetting letter body, so typing doesn't reset cursor
  useEffect(() => {
    if (isOpen && visit && bodyEditorRef.current && !bodyContentInitializedRef.current) {
      bodyEditorRef.current.innerHTML = editorContent;
      if (editorContent) bodyContentInitializedRef.current = true;
    }
  }, [isOpen, visit, editorContent]);

  if (!visit) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Generate Visit Letter">
        <p>No visit selected.</p>
      </Modal>
    );
  }

  const doSendLetter = async (vRemarks, options = {}) => {
    const { closeAfter = true, isDraft = false } = options;
    await visitService.sendConfirmationLetter(visit.scheduledVisitID, {
      letterHeader: headerText,
      letterBody: editorContent,
      letterFooter: footerText,
      piEmail: null,
      vRemarks: vRemarks || undefined,
      isDraft,
    });
    if (onGenerated) onGenerated();
    setToast({ type: 'success', message: commonMessages.authSuccess });
    if (closeAfter) onClose();
  };

  const validateLetterBody = () => {
    const text = getBodyPlainText(editorContent);
    if (!text) {
      setLetterBodyError(visitMessages.letterBodyEmpty);
      return false;
    }
    if (!text) {
      setLetterBodyError(visitMessages.letterBodyShort);
      return false;
    }
    setLetterBodyError('');
    return true;
  };

  const handlePreview = () => {
    setLetterBodyError('');
    const text = getBodyPlainText(editorContent);
    if (!text) {
      setLetterBodyError(visitMessages.letterPreviewEmpty);
      return;
    }
    if (!text) {
      setLetterBodyError(visitMessages.letterBodyShort);
      return;
    }
    setLetterBodyError('');
    setShowPreview(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!visit.scheduledVisitID) return;
    setLetterBodyError('');
    if (!validateLetterBody()) return;
    eSignRemarksRef.current = '';
    setShowESignModal(true);
  };

  const runAfterESign = () => {
    const vRemarks = eSignRemarksRef.current || '';
    const isSaveAndContinue = saveAndContinueModeRef.current;
    saveAndContinueModeRef.current = false;
    setShowESignModal(false);
    setSaving(true);
    doSendLetter(vRemarks, { closeAfter: !isSaveAndContinue, isDraft: isSaveAndContinue })
      .catch((err) => {
        console.error('Error generating visit letter:', err);
        const msg = err.response?.data?.message ?? err.response?.data?.error ?? err.message ?? 'Unable to create or update visit letter. Please try again.';
        setToast({ type: 'error', message: msg });
      })
      .finally(() => setSaving(false));
  };

  const handleSaveAndContinue = (e) => {
    e.preventDefault();
    if (!visit.scheduledVisitID) return;
    setLetterBodyError('');
    const text = getBodyPlainText(editorContent);
    if (!text) {
      setLetterBodyError(visitMessages.noDataToSave);
      return;
    }
    setSaving(true);
    doSendLetter('', { isDraft: true, closeAfter: true })
      .catch((err) => {
        console.error('Error saving visit letter draft:', err);
        const msg = err.response?.data?.message ?? err.response?.data?.error ?? err.message ?? 'Unable to save visit letter draft. Please try again.';
        setToast({ type: 'error', message: msg });
      })
      .finally(() => setSaving(false));
  };

  const handleClear = () => {
    setLetterBodyError('');
    bodyContentInitializedRef.current = false;
    setEditorContent('');
    setHistory(['']);
    setHistoryIndex(0);
    if (bodyEditorRef.current) {
      bodyEditorRef.current.innerHTML = '';
    }
  };

  const visitTypeLabel = VISIT_TYPES[visit.visitType] || visit.visitType || '';

  // Helper to push new body content into history (for undo/redo)
  const pushHistory = (content) => {
    setHistory(prev => {
      const trimmed = historyIndex >= 0 ? prev.slice(0, historyIndex + 1) : [];
      if (trimmed.length > 0 && trimmed[trimmed.length - 1] === content) {
        return trimmed;
      }
      const next = [...trimmed, content];
      setHistoryIndex(next.length - 1);
      return next;
    });
  };

  // Simple rich text commands using document.execCommand
  const applyCommand = (command, value = null) => {
    if (!bodyEditorRef.current) return;
    bodyEditorRef.current.focus();
    document.execCommand(command, false, value);
    // Update state with latest HTML after command
    const html = bodyEditorRef.current.innerHTML;
    setEditorContent(html);
    pushHistory(html);
  };

  const handleBodyInput = (e) => {
    const html = e.currentTarget.innerHTML;
    setEditorContent(html);
    setLetterBodyError('');
    pushHistory(html);
  };

  const handleUndoClick = () => {
    if (historyIndex <= 0) return;
    const newIndex = historyIndex - 1;
    setHistoryIndex(newIndex);
    const html = history[newIndex];
    setEditorContent(html);
    if (bodyEditorRef.current) {
      bodyEditorRef.current.innerHTML = html;
    }
  };

  const handleRedoClick = () => {
    if (historyIndex === -1 || historyIndex >= history.length - 1) return;
    const newIndex = historyIndex + 1;
    setHistoryIndex(newIndex);
    const html = history[newIndex];
    setEditorContent(html);
    if (bodyEditorRef.current) {
      bodyEditorRef.current.innerHTML = html;
    }
  };

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="large"
      title="Generate Visit Letter"
      className="generate-letter-modal"
    >
      <form onSubmit={handleSubmit}>
        <VisitModalHeader
          projectCode={visit.projectCode}
          siteName={visit.siteName}
          siteCode={visit.siteCode ?? visit.SiteCode}
          piName={resolvedPiName ?? visit.piName ?? visit.PIName ?? visit.pIName}
        />

        <div className="form-group">
          <label className="form-label">Visit Type</label>
          <input
            type="text"
            className="form-input"
            value={visitTypeLabel}
            disabled
          />
        </div>

        {/* Header part (read-only) */}
        <div className="form-group generate-letter-header-block">
          
          <textarea
            className="form-textarea generate-letter-template"
            rows="6"
            value={headerText}
            readOnly
          />
        </div>

        {/* Rich text toolbar + editor for body */}
        <div className="form-group">
          

          {/* Toolbar inspired by wireframe */}
          <div className="richtext-toolbar">
            <button type="button" className="rt-btn" onClick={() => applyCommand('bold')}>
              B
            </button>
            <button type="button" className="rt-btn" onClick={() => applyCommand('italic')}>
              I
            </button>
            <button type="button" className="rt-btn" onClick={() => applyCommand('underline')}>
              U
            </button>
            <button type="button" className="rt-btn" onClick={() => applyCommand('insertUnorderedList')}>
              •
            </button>
            <button type="button" className="rt-btn" onClick={() => applyCommand('insertOrderedList')}>
              1.
            </button>
            <button type="button" className="rt-btn" onClick={() => applyCommand('justifyLeft')}>
              L
            </button>
            <button type="button" className="rt-btn" onClick={() => applyCommand('justifyCenter')}>
              C
            </button>
            <button type="button" className="rt-btn" onClick={() => applyCommand('justifyRight')}>
              R
            </button>
            <button type="button" className="rt-btn" onClick={handleUndoClick}>
              ↺
            </button>
            <button type="button" className="rt-btn" onClick={handleRedoClick}>
              ↻
            </button>
            <div className="rt-select-wrap">
              <span className="rt-select-label">{blockFormat === 'h1' ? 'Heading 1' : blockFormat === 'h2' ? 'Heading 2' : blockFormat === 'h3' ? 'Heading 3' : 'Normal'}</span>
              <select
                className="rt-select"
                onChange={(e) => {
                  const value = e.target.value;
                  if (!value) return;
                  setBlockFormat(value);
                  applyCommand('formatBlock', value);
                }}
                value={blockFormat}
                aria-label="Block format"
              >
                <option value="p">Normal</option>
                <option value="h1">Heading 1</option>
                <option value="h2">Heading 2</option>
                <option value="h3">Heading 3</option>
              </select>
            </div>
            <div className="rt-select-wrap rt-select-wrap--font">
              <span className="rt-select-label">{fontFamily || 'Arial'}</span>
              <select
                className="rt-select"
                onChange={(e) => {
                  const value = e.target.value;
                  if (!value) return;
                  applyCommand('fontName', value);
                  setFontFamily(value);
                }}
                value={fontFamily}
                aria-label="Font family"
              >
                <option value="">Font Family</option>
                <option value="Arial">Arial</option>
                <option value="Times New Roman">Times New Roman</option>
                <option value="Calibri">Calibri</option>
                <option value="Georgia">Georgia</option>
              </select>
            </div>
          </div>

          <div
            ref={bodyEditorRef}
            className="form-textarea richtext-editor"
            contentEditable
            suppressContentEditableWarning
            onInput={handleBodyInput}
          />
          {letterBodyError && (
            <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>{letterBodyError}</div>
          )}
        </div>

        {/* Footer part (read-only) */}
        <div className="form-group generate-letter-footer-block">
          
          <textarea
            className="form-textarea generate-letter-template"
            rows="4"
            value={footerText}
            readOnly
          />
        </div>

        <div className="generate-letter-footer">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handlePreview}
            disabled={saving}
          >
            Preview
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSaveAndContinue}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Draft Save'}
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving}
          >
            {saving ? 'Generating...' : 'Submit'}
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleClear}
            disabled={saving}
          >
            Clear
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </form>
    </Modal>

    {/* Visit Letter Preview Modal */}
    <Modal
      isOpen={showPreview}
      onClose={() => setShowPreview(false)}
      size="large"
      title="Generate Visit Letter Preview"
      className="generate-letter-preview-modal"
      footer={
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => setShowPreview(false)}
        >
          Close
        </button>
      }
    >
      <div className="generate-letter-preview-content">
        {headerText && (
          <pre className="generate-letter-preview-header">{headerText}</pre>
        )}
        <div
          className="generate-letter-preview-body"
          dangerouslySetInnerHTML={{ __html: editorContent }}
        />
        {footerText && (
          <pre className="generate-letter-preview-footer">{footerText}</pre>
        )}
      </div>
    </Modal>

    {showESignModal && (
      <ElectronicSign
        show={true}
        onClose={() => { setShowESignModal(false); eSignRemarksRef.current = ''; }}
        onAuthSuccess={(vRemarks) => { eSignRemarksRef.current = vRemarks || ''; }}
        onSave={runAfterESign}
      />
    )}
    {toast && (
      <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 10000 }}>
        <Alert type={toast.type} message={toast.message} onClose={() => setToast(null)} />
      </div>
    )}
    </>
  );
};

export default GenerateVisitLetterModal;

